<template>
    <div class="nbv2" :class="{ 'nbv2--open': isOpen }">
        <button
            ref="trigger"
            type="button"
            class="nbv2-trigger"
            :class="{ 'has-unread': unreadCount > 0 }"
            :aria-label="$t('topbar.notifications.aria') || 'Notifications'"
            :aria-expanded="isOpen ? 'true' : 'false'"
            @click="toggle"
        >
            <i class="fal fa-bell"></i>
            <span v-if="unreadCount > 0" class="nbv2-badge" aria-hidden="true">
                {{ badgeLabel }}
            </span>
            <span v-if="unreadCount > 0" class="sr-only">
                {{ $t('topbar.notifications.unread_count', { count: unreadCount }) || `${unreadCount} non lues` }}
            </span>
        </button>

        <portal to="topbar-portal" v-if="isOpen">
            <transition name="nbv2-fade" appear>
                <div
                    ref="panel"
                    class="nbv2-panel"
                    :style="panelStyle"
                    role="dialog"
                    :aria-label="$t('topbar.notifications.title') || 'Notifications'"
                >
                    <header class="nbv2-panel-header">
                        <div class="nbv2-panel-titles">
                            <strong class="nbv2-panel-title">
                                {{ $t('topbar.notifications.title') || 'Notifications' }}
                            </strong>
                            <span v-if="unreadCount > 0" class="nbv2-panel-subtitle">
                                {{ $t('topbar.notifications.unread_count', { count: unreadCount }) || `${unreadCount} non lues` }}
                            </span>
                        </div>
                        <button
                            v-if="unreadCount > 0"
                            type="button"
                            class="nbv2-mark-all"
                            @click="markAllRead"
                        >
                            {{ $t('topbar.notifications.mark_all_read') || 'Tout marquer comme lu' }}
                        </button>
                    </header>

                    <div v-if="notifications.length" class="nbv2-list">
                        <button
                            v-for="n in notifications"
                            :key="n.id"
                            type="button"
                            class="nbv2-item"
                            :class="{ 'is-unread': !n.read }"
                            @click="onItemClick(n)"
                        >
                            <span class="nbv2-item-icon" :class="iconKindClass(n.kind)">
                                <i :class="iconClass(n)"></i>
                            </span>
                            <span class="nbv2-item-body">
                                <span class="nbv2-item-title">{{ n.title }}</span>
                                <span class="nbv2-item-time">
                                    <template v-if="n.postId">
                                        <span class="nbv2-item-ref">{{ $t('topbar.notifications.ticket_ref', { id: n.postId }) || `Ticket #${n.postId}` }}</span>
                                        <span class="nbv2-item-sep" aria-hidden="true">·</span>
                                    </template>
                                    {{ formatTime(n.createdAt) }}
                                </span>
                            </span>
                            <span v-if="!n.read" class="nbv2-item-dot" aria-hidden="true"></span>
                        </button>
                    </div>

                    <div v-else class="nbv2-empty">
                        <i class="fal fa-bell-slash nbv2-empty-icon"></i>
                        <span class="nbv2-empty-title">
                            {{ $t('topbar.notifications.empty_title') || 'Aucune notification' }}
                        </span>
                        <span class="nbv2-empty-desc">
                            {{ $t('topbar.notifications.empty_desc') || 'Vous serez prévenu·e ici dès qu\'il se passe quelque chose.' }}
                        </span>
                    </div>

                    <footer class="nbv2-panel-footer">
                        <router-link
                            to="/notifications"
                            class="nbv2-see-all"
                            @click.native="isOpen = false"
                        >
                            {{ $t('topbar.notifications.see_all') || 'Tout voir' }}
                            <i class="fal fa-arrow-right"></i>
                        </router-link>
                        <button
                            v-if="notifications.length"
                            type="button"
                            class="nbv2-clear"
                            @click="clearAll"
                        >
                            <i class="fal fa-trash-alt"></i>
                            {{ $t('topbar.notifications.clear_all') || 'Tout effacer' }}
                        </button>
                    </footer>
                </div>
            </transition>
        </portal>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState, mapActions } from 'vuex'

export default {
    name: 'NotificationsBellV2',
    data() {
        return {
            isOpen: false,
            panelStyle: {}
        }
    },
    computed: {
        ...mapState('notifications', { notifications: (s) => s.list }),
        unreadCount() {
            return this.notifications.filter((n) => !n.read).length
        },
        badgeLabel() {
            if (this.unreadCount > 99) return '99+'
            return String(this.unreadCount)
        }
    },
    watch: {
        isOpen(open) {
            if (open) {
                this.updatePosition()
                this.$nextTick(this.updatePosition)
                document.addEventListener('mousedown', this.onOutsideClick)
                document.addEventListener('keydown', this.onKeydown)
                window.addEventListener('scroll', this.updatePosition, true)
                window.addEventListener('resize', this.updatePosition)
            } else {
                document.removeEventListener('mousedown', this.onOutsideClick)
                document.removeEventListener('keydown', this.onKeydown)
                window.removeEventListener('scroll', this.updatePosition, true)
                window.removeEventListener('resize', this.updatePosition)
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onOutsideClick)
        document.removeEventListener('keydown', this.onKeydown)
        window.removeEventListener('scroll', this.updatePosition, true)
        window.removeEventListener('resize', this.updatePosition)
    },
    methods: {
        toggle() {
            this.isOpen = !this.isOpen
        },
        close() {
            this.isOpen = false
        },
        onOutsideClick(e) {
            const insideTrigger = this.$el && this.$el.contains(e.target)
            const insidePanel = this.$refs.panel && this.$refs.panel.contains(e.target)
            if (!insideTrigger && !insidePanel) this.close()
        },
        onKeydown(e) {
            if (e.key === 'Escape') this.close()
        },
        updatePosition() {
            if (!this.isOpen || !this.$refs.trigger) return
            const rect = this.$refs.trigger.getBoundingClientRect()
            const vw = window.innerWidth
            const vh = window.innerHeight
            const panelHeight = (this.$refs.panel && this.$refs.panel.offsetHeight) || 420
            const gap = 8
            const edge = 12
            const width = 360
            const spaceBelow = vh - rect.bottom
            const flip = spaceBelow < panelHeight + gap && rect.top > spaceBelow
            let left = rect.right - width
            if (left < edge) left = edge
            if (left + width > vw - edge) left = vw - width - edge
            const style = {
                position: 'fixed',
                left: left + 'px',
                width: width + 'px',
                zIndex: 10000
            }
            if (flip) {
                style.bottom = (vh - rect.top + gap) + 'px'
            } else {
                style.top = (rect.bottom + gap) + 'px'
            }
            this.panelStyle = style
        },
        ...mapActions('notifications', ['markRead', 'markAllRead']),
        clearAll() {
            this.$store.dispatch('notifications/clearAll')
            this.close()
        },
        onItemClick(n) {
            if (!n.read) this.markRead(n.id)
            if (n.link) {
                this.close()
                this.$router.push(n.link).catch(() => {})
            }
        },
        iconClass(n) {
            if (n.icon) return n.icon
            switch (n.kind) {
                case 'success': return 'fal fa-check-circle'
                case 'warning': return 'fal fa-exclamation-triangle'
                case 'danger': return 'fal fa-times-circle'
                case 'message': return 'fal fa-comment-alt'
                case 'ticket': return 'fal fa-ticket'
                default: return 'fal fa-info-circle'
            }
        },
        iconKindClass(kind) {
            return `nbv2-item-icon--${kind || 'info'}`
        },
        formatTime(iso) {
            if (!iso) return ''
            const date = new Date(iso)
            if (Number.isNaN(date.getTime())) return ''
            const diff = (Date.now() - date.getTime()) / 1000
            if (diff < 60) return this.$t('topbar.notifications.time_now') || 'à l\'instant'
            if (diff < 3600) {
                const mins = Math.floor(diff / 60)
                return this.$t('topbar.notifications.time_minutes', { count: mins }) || `il y a ${mins} min`
            }
            if (diff < 86400) {
                const hours = Math.floor(diff / 3600)
                return this.$t('topbar.notifications.time_hours', { count: hours }) || `il y a ${hours} h`
            }
            return date.toLocaleDateString(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'fr')
        }
    }
}
</script>

<style scoped>
.nbv2 {
    position: relative;
    display: inline-flex;
}

.nbv2-trigger {
    width: 34px;
    height: 34px;
    border-radius: var(--v2-radius-full);
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0;
    transition:
        border-color var(--v2-transition-fast),
        background var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.nbv2-trigger:hover {
    border-color: var(--v2-border-strong);
    color: var(--v2-text-1);
    background: var(--v2-surface-1);
}

.nbv2-trigger:focus-visible {
    outline: none;
    box-shadow: var(--v2-shadow-focus);
}

.nbv2--open .nbv2-trigger,
.nbv2-trigger.has-unread {
    color: var(--v2-primary);
    border-color: var(--v2-primary);
}

.nbv2-trigger i {
    font-size: 0.95rem;
}

.nbv2-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--v2-danger);
    color: #fff;
    font-size: 0.62rem;
    font-weight: var(--v2-font-weight-semibold);
    line-height: 16px;
    text-align: center;
    box-shadow: 0 0 0 2px var(--v2-surface-0);
    box-sizing: border-box;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* ===== Panel (rendered through portal — global styles below) ===== */
</style>

<style>
/* Portaled panel lives outside scoped boundary, so styles must be global. */
.nbv2-panel {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow:
        0 20px 40px rgba(15, 23, 42, 0.14),
        0 6px 14px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 100px);
    font-family: var(--v2-font-sans);
}

.nbv2-panel-header {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-3) var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border-subtle);
    flex-shrink: 0;
}

.nbv2-panel-titles {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.nbv2-panel-title {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.3;
}

.nbv2-panel-subtitle {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-medium);
    margin-top: 1px;
}

.nbv2-mark-all {
    border: none;
    background: transparent;
    color: var(--v2-primary);
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast);
    flex-shrink: 0;
}

.nbv2-mark-all:hover {
    background: var(--v2-primary-soft);
}

.nbv2-list {
    overflow-y: auto;
    padding: 4px;
    flex: 1;
    min-height: 0;
}

.nbv2-item {
    display: flex;
    align-items: flex-start;
    gap: var(--v2-space-3);
    width: 100%;
    padding: 10px;
    border-radius: var(--v2-radius-md);
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: var(--v2-font-sans);
    transition: background var(--v2-transition-fast);
    position: relative;
}

.nbv2-item:hover {
    background: var(--v2-surface-2);
}

.nbv2-item.is-unread {
    background: var(--v2-primary-soft);
}

.nbv2-item.is-unread:hover {
    background: rgba(8, 145, 178, 0.14);
}

.nbv2-item-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--v2-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.85rem;
    color: #fff;
}

.nbv2-item-icon i {
    color: #fff !important;
}

.nbv2-item-icon--info { background: var(--v2-primary); }
.nbv2-item-icon--success { background: #10b981; }
.nbv2-item-icon--warning { background: var(--v2-warning); }
.nbv2-item-icon--danger { background: var(--v2-danger); }
.nbv2-item-icon--message { background: #8b5cf6; }
.nbv2-item-icon--ticket { background: var(--v2-primary); }

.nbv2-item-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
}

.nbv2-item-title {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nbv2-item-time {
    font-size: 0.68rem;
    color: var(--v2-text-4);
    font-weight: var(--v2-font-weight-medium);
    margin-top: 2px;
}

.nbv2-item-ref {
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-semibold);
}

.nbv2-item-sep {
    margin: 0 4px;
    color: var(--v2-text-4);
}

.nbv2-item-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v2-primary);
    flex-shrink: 0;
    margin-top: 6px;
}

.nbv2-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-6) var(--v2-space-4);
    text-align: center;
    gap: 4px;
}

.nbv2-empty-icon {
    font-size: 1.6rem;
    color: var(--v2-text-4);
    margin-bottom: var(--v2-space-2);
}

.nbv2-empty-title {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
}

.nbv2-empty-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-4);
    max-width: 240px;
    line-height: 1.4;
}

.nbv2-panel-footer {
    flex-shrink: 0;
    border-top: 1px solid var(--v2-border-subtle);
    padding: 6px;
}

.nbv2-clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    justify-content: center;
    padding: 8px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.nbv2-clear:hover {
    background: var(--v2-danger-soft);
    color: var(--v2-danger);
}

.nbv2-see-all {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px;
    background: transparent;
    color: var(--v2-primary);
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    text-decoration: none !important;
    border-radius: var(--v2-radius-sm);
    transition: background var(--v2-transition-fast);
}

.nbv2-see-all:hover {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.nbv2-see-all i {
    font-size: 0.7rem;
    color: inherit !important;
}

/* Transition */
.nbv2-fade-enter-active,
.nbv2-fade-leave-active {
    transition: opacity var(--v2-transition-base),
        transform var(--v2-transition-base);
}

.nbv2-fade-enter,
.nbv2-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
}

@media (max-width: 480px) {
    .nbv2-panel {
        max-width: calc(100vw - 24px);
    }
}
</style>
